import { FC } from "preact/compat";
import { useComputed, useSignal } from "@preact/signals";
import { useNavigate } from "react-router-dom";
import { useApp } from "~/core";
import { me } from "~/core/store";
import { Button, ClassicDialog } from "~/components";
import Purchases from "./Purchases";
import Tasks from "./Tasks";
import { CreateFromBudgetProps } from "./interfaces";
import {
    hasPurchaseLines as computeHasPurchaseLines,
    tasksConversionDone,
} from "./utils";

const CreateFromBudget: FC<CreateFromBudgetProps> = ({
    budget,
    renderTrigger,
}) => {
    const { api } = useApp();
    const isOpen = useSignal(false);
    const loading = useSignal(false);
    const step = useSignal(0);
    const navigate = useNavigate();

    const hasPurchaseLines = useComputed(() => computeHasPurchaseLines(budget));

    const open = () => {
        const tasksDone = tasksConversionDone(api, budget);

        step.value = tasksDone && hasPurchaseLines.peek() ? 1 : 0;
        isOpen.value = true;
    };

    const close = () => {
        isOpen.value = false;
    };

    const handleTasksNext = () => {
        if (hasPurchaseLines.peek()) {
            step.value = 1;
        } else {
            close();
            navigate(me(`/projects/${budget.project.toString()}/tasks`));
        }
    };

    const handlePurchasesDone = () => {
        close();
        navigate(me(`/projects/${budget.project.toString()}/purchase`));
    };

    return (
        <div>
            <ClassicDialog
                size="2xl"
                loading={loading}
                show={isOpen}
                title="Create from budget"
                contentClassName="flex h-[70vh] max-h-[calc(100vh-158px)] flex-col"
            >
                {step.value === 0 ? (
                    <Tasks
                        budget={budget}
                        loading={loading}
                        hasNext={hasPurchaseLines.value}
                        onClose={close}
                        onNext={handleTasksNext}
                    />
                ) : (
                    <Purchases
                        budget={budget}
                        loading={loading}
                        hasNext={false}
                        onClose={close}
                        onNext={handlePurchasesDone}
                    />
                )}
            </ClassicDialog>
            {renderTrigger ? (
                renderTrigger(open)
            ) : (
                <Button variant="primary" onClick={open}>
                    Create from budget
                </Button>
            )}
        </div>
    );
};

export default CreateFromBudget;
